import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { fmtMoney, fmtCur, fmtTotals, sumByCurrency } from "@/lib/format";
import { Trash, Plus, ArrowLeft, Camera, X, BatteryVerticalHigh } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p, w) => `${BACKEND_URL}/api/files/${p.path}${w ? `?w=${w}` : ""}`;

export default function OrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [masters, setMasters] = useState([]);
  const [services, setServices] = useState([]);
  const [parts, setParts] = useState([]);

  const [clientDlg, setClientDlg] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "", email: "" });
  const [vehicleDlg, setVehicleDlg] = useState(false);
  const emptyVehicle = { make: "", model: "", year: "", vin: "", plate: "", mileage: "", photo: null };
  const [newVehicle, setNewVehicle] = useState(emptyVehicle);

  const [form, setForm] = useState({
    client_id: "", vehicle_id: "", master_id: "", complaint: "", mileage: "",
    notes: "", services: [], parts: [],
    diagnostics_text: "", diagnostics_photos: [],
    repair_text: "", repair_photos: [],
    battery_segments: "", battery_bad_segments: [],
    exit_diagnostics_text: "", exit_diagnostics_photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [batteryDlg, setBatteryDlg] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/clients"), api.get("/masters"), api.get("/services"), api.get("/parts"),
    ]).then(([c, m, s, p]) => {
      setClients(c.data); setMasters(m.data); setServices(s.data); setParts(p.data);
    });
    if (editing) {
      api.get(`/orders/${id}`).then((r) => {
        const o = r.data;
        setForm({
          client_id: o.client_id, vehicle_id: o.vehicle_id, master_id: o.master_id || "",
          complaint: o.complaint || "", mileage: o.mileage || "", notes: o.notes || "",
          services: o.services || [], parts: o.parts || [],
          diagnostics_text: o.diagnostics_text || "", diagnostics_photos: o.diagnostics_photos || [],
          repair_text: o.repair_text || "", repair_photos: o.repair_photos || [],
          battery_segments: o.battery_segments || "", battery_bad_segments: o.battery_bad_segments || [],
          exit_diagnostics_text: o.exit_diagnostics_text || "", exit_diagnostics_photos: o.exit_diagnostics_photos || [],
        });
      });
    }
  }, [id, editing]);

  useEffect(() => {
    if (form.client_id) api.get("/vehicles", { params: { client_id: form.client_id } }).then((r) => setVehicles(r.data));
    else setVehicles([]);
  }, [form.client_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      setForm((f) => ({ ...f, diagnostics_photos: [...f.diagnostics_photos, ...uploaded] }));
      toast.success(`Завантажено фото: ${uploaded.length}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) =>
    setForm((f) => ({ ...f, diagnostics_photos: f.diagnostics_photos.filter((_, i) => i !== idx) }));

  const uploadExitPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      setForm((f) => ({ ...f, exit_diagnostics_photos: [...f.exit_diagnostics_photos, ...uploaded] }));
      toast.success(`Завантажено фото: ${uploaded.length}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const removeExitPhoto = (idx) =>
    setForm((f) => ({ ...f, exit_diagnostics_photos: f.exit_diagnostics_photos.filter((_, i) => i !== idx) }));

  const uploadRepairPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      setForm((f) => ({ ...f, repair_photos: [...f.repair_photos, ...uploaded] }));
      toast.success(`Завантажено фото: ${uploaded.length}`);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const removeRepairPhoto = (idx) =>
    setForm((f) => ({ ...f, repair_photos: f.repair_photos.filter((_, i) => i !== idx) }));

  const openBatteryTable = () => {
    const n = parseInt(form.battery_segments, 10);
    if (!n || n < 1) return toast.error("Вкажіть кількість сегментів (число)");
    if (n > 500) return toast.error("Забагато сегментів (макс. 500)");
    setBatteryDlg(true);
  };

  const toggleSegment = (n) =>
    setForm((f) => ({
      ...f,
      battery_bad_segments: f.battery_bad_segments.includes(n)
        ? f.battery_bad_segments.filter((x) => x !== n)
        : [...f.battery_bad_segments, n].sort((a, b) => a - b),
    }));

  const submit = async () => {
    if (!form.client_id || !form.vehicle_id) return toast.error("Оберіть клієнта та авто");
    const payload = {
      ...form,
      mileage: form.mileage ? Number(form.mileage) : null,
      battery_segments: form.battery_segments ? Number(form.battery_segments) : 0,
    };
    try {
      if (editing) { await api.put(`/orders/${id}`, payload); toast.success("Наряд оновлено"); navigate(`/orders/${id}`); }
      else { const r = await api.post("/orders", payload); toast.success("Наряд створено"); navigate(`/orders/${r.data.id}`); }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const createClient = async () => {
    if (!newClient.name) return toast.error("Вкажіть імʼя клієнта");
    try {
      const r = await api.post("/clients", newClient);
      setClients((prev) => [r.data, ...prev]);
      set("client_id", r.data.id);
      set("vehicle_id", "");
      toast.success("Клієнта створено");
      setClientDlg(false);
      setNewClient({ name: "", phone: "", email: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const createVehicle = async () => {
    if (!form.client_id) return toast.error("Спершу оберіть клієнта");
    if (!newVehicle.make && !newVehicle.plate) return toast.error("Вкажіть марку або держномер");
    try {
      const payload = {
        ...newVehicle, client_id: form.client_id,
        year: newVehicle.year ? Number(newVehicle.year) : null,
        mileage: newVehicle.mileage ? Number(newVehicle.mileage) : null,
      };
      const r = await api.post("/vehicles", payload);
      setVehicles((prev) => [r.data, ...prev]);
      set("vehicle_id", r.data.id);
      toast.success("Авто додано");
      setVehicleDlg(false);
      setNewVehicle(emptyVehicle);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const addService = (sid) => {
    const s = services.find((x) => x.id === sid);
    if (!s) return;
    set("services", [...form.services, { service_id: s.id, name: s.name, qty: 1, hours: s.norm_hours, price: s.price, currency: s.price_currency || "UAH" }]);
  };
  const addPart = (pid) => {
    const p = parts.find((x) => x.id === pid);
    if (!p) return;
    set("parts", [...form.parts, { part_id: p.id, name: p.name, qty: 1, price: p.price, currency: p.price_currency || "UAH" }]);
  };
  const updRow = (type, i, k, v) => {
    const arr = [...form[type]];
    arr[i] = { ...arr[i], [k]: (k === "name" || k === "currency") ? v : Number(v) };
    set(type, arr);
  };
  const delRow = (type, i) => set(type, form[type].filter((_, idx) => idx !== i));

  const totals = sumByCurrency([...form.services, ...form.parts]);

  const RowTable = ({ type, cols }) => (
    <div className="space-y-2">
      {form[type].map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${type}-row-${i}`}>
          <Input className="col-span-4" value={r.name} onChange={(e) => updRow(type, i, "name", e.target.value)} />
          <Input className="col-span-2" type="number" value={r.qty} onChange={(e) => updRow(type, i, "qty", e.target.value)} placeholder="К-сть" />
          <Input className="col-span-3" type="number" value={r.price} onChange={(e) => updRow(type, i, "price", e.target.value)} placeholder="Ціна" />
          <Select value={r.currency || "UAH"} onValueChange={(v) => updRow(type, i, "currency", v)}>
            <SelectTrigger className="col-span-2" data-testid={`${type}-currency-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="UAH">₴</SelectItem><SelectItem value="USD">$</SelectItem><SelectItem value="EUR">€</SelectItem></SelectContent>
          </Select>
          <button className="col-span-1 text-destructive flex justify-center" onClick={() => delRow(type, i)} data-testid={`del-${type}-${i}`}>
            <Trash size={18} />
          </button>
        </div>
      ))}
      {form[type].length === 0 && <div className="text-sm text-muted-foreground">Нічого не додано</div>}
    </div>
  );

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/orders")} className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="back-btn">
        <ArrowLeft size={16} /> До нарядів
      </button>
      <PageHeader title={editing ? "Редагування наряду" : "Новий наряд"} />

      <div className="space-y-5">
        <Card className="p-6">
          <h3 className="font-head font-bold mb-4">Клієнт та авто</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Клієнт</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={form.client_id} onValueChange={(v) => { set("client_id", v); set("vehicle_id", ""); }}>
                  <SelectTrigger className="flex-1" data-testid="select-client"><SelectValue placeholder="Оберіть клієнта" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" className="shrink-0"
                  onClick={() => setClientDlg(true)} data-testid="new-client-btn" title="Створити клієнта">
                  <Plus size={18} />
                </Button>
              </div>
            </div>
            <div>
              <Label>Автомобіль</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)} disabled={!form.client_id}>
                  <SelectTrigger className="flex-1" data-testid="select-vehicle"><SelectValue placeholder="Оберіть авто" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.make} {v.model} · {v.plate}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" className="shrink-0" disabled={!form.client_id}
                  onClick={() => setVehicleDlg(true)} data-testid="new-vehicle-btn" title="Додати авто">
                  <Plus size={18} />
                </Button>
              </div>
            </div>
            <div>
              <Label>Майстер</Label>
              <Select value={form.master_id} onValueChange={(v) => set("master_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="select-master"><SelectValue placeholder="Призначити майстра" /></SelectTrigger>
                <SelectContent>{masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Пробіг, км</Label>
              <Input className="mt-1.5" type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="input-mileage" />
            </div>
          </div>
          <div className="mt-4">
            <Label>Скарги / зауваження</Label>
            <Textarea className="mt-1.5" value={form.complaint} onChange={(e) => set("complaint", e.target.value)} data-testid="input-complaint" />
          </div>
        </Card>

        <Card className="p-6" data-testid="diagnostics-card">
          <h3 className="font-head font-bold mb-4">Вхідна діагностика</h3>
          <div>
            <Label>Опис стану / діагностика</Label>
            <Textarea className="mt-1.5" rows={4} value={form.diagnostics_text}
              onChange={(e) => set("diagnostics_text", e.target.value)}
              placeholder="Опишіть виявлені несправності, стан авто при прийманні..."
              data-testid="diagnostics-text" />
          </div>
          <div className="mt-4">
            <Label>Фотографії</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.diagnostics_photos.map((p, i) => (
                <div key={p.id || i} className="relative group aspect-square rounded-md overflow-hidden border border-border bg-secondary"
                  data-testid={`diag-photo-${i}`}>
                  <img src={photoUrl(p, 400)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
              <label data-testid="upload-photo-label"
                className="aspect-square rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 cursor-pointer text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                <Camera size={26} />
                <span className="text-xs">{uploading ? "Завантаження..." : "Додати фото"}</span>
                <input type="file" accept="image/*" multiple hidden disabled={uploading}
                  onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }}
                  data-testid="upload-photo-input" />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Можна завантажити необмежену кількість зображень (до 12 МБ кожне).</p>
          </div>

          <div className="mt-6 pt-5 border-t border-border">
            <Label>Діагностика батареї</Label>
            <div className="flex flex-wrap items-end gap-3 mt-1.5">
              <div className="w-40">
                <span className="text-xs text-muted-foreground">Кількість сегментів (S)</span>
                <Input type="number" min="1" value={form.battery_segments}
                  onChange={(e) => set("battery_segments", e.target.value)}
                  onBlur={() => { if (parseInt(form.battery_segments, 10) > 0) setBatteryDlg(true); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); openBatteryTable(); } }}
                  placeholder="напр. 96" className="mt-1" data-testid="battery-segments" />
              </div>
              <Button type="button" variant="outline" onClick={openBatteryTable} data-testid="open-battery-table"
                disabled={!parseInt(form.battery_segments, 10)}>
                <BatteryVerticalHigh size={18} className="mr-1" /> Таблиця сегментів
              </Button>
            </div>
            {form.battery_bad_segments.length > 0 && (
              <div className="mt-3 text-sm" data-testid="battery-summary">
                <span className="text-muted-foreground">Несправні сегменти ({form.battery_bad_segments.length}): </span>
                <span className="font-semibold text-destructive">{form.battery_bad_segments.join(", ")}</span>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6" data-testid="repair-card">
          <h3 className="font-head font-bold mb-4">Ремонтні роботи</h3>
          <div>
            <Label>Опис виконаних робіт</Label>
            <Textarea className="mt-1.5" rows={4} value={form.repair_text}
              onChange={(e) => set("repair_text", e.target.value)}
              placeholder="Опишіть проведені ремонтні роботи, замінені деталі, процес..."
              data-testid="repair-text" />
          </div>
          <div className="mt-4">
            <Label>Медіафайли</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.repair_photos.map((p, i) => (
                <div key={p.id || i} className="relative group aspect-square rounded-md overflow-hidden border border-border bg-secondary"
                  data-testid={`repair-photo-${i}`}>
                  <img src={photoUrl(p, 400)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeRepairPhoto(i)} data-testid={`remove-repair-photo-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
              <label data-testid="upload-repair-photo-label"
                className="aspect-square rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 cursor-pointer text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                <Camera size={26} />
                <span className="text-xs">{uploading ? "Завантаження..." : "Додати медіа"}</span>
                <input type="file" accept="image/*" multiple hidden disabled={uploading}
                  onChange={(e) => { uploadRepairPhotos(e.target.files); e.target.value = ""; }}
                  data-testid="upload-repair-photo-input" />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Можна завантажити необмежену кількість зображень (до 12 МБ кожне).</p>
          </div>
        </Card>

        <Card className="p-6" data-testid="exit-diagnostics-card">
          <h3 className="font-head font-bold mb-4">Вихідна діагностика</h3>
          <div>
            <Label>Заключення майстра</Label>
            <Textarea className="mt-1.5" rows={4} value={form.exit_diagnostics_text}
              onChange={(e) => set("exit_diagnostics_text", e.target.value)}
              placeholder="Опишіть виконані роботи, результат діагностики, рекомендації клієнту..."
              data-testid="exit-diagnostics-text" />
          </div>
          <div className="mt-4">
            <Label>Фотографії</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.exit_diagnostics_photos.map((p, i) => (
                <div key={p.id || i} className="relative group aspect-square rounded-md overflow-hidden border border-border bg-secondary"
                  data-testid={`exit-diag-photo-${i}`}>
                  <img src={photoUrl(p, 400)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeExitPhoto(i)} data-testid={`remove-exit-photo-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
              <label data-testid="upload-exit-photo-label"
                className="aspect-square rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 cursor-pointer text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                <Camera size={26} />
                <span className="text-xs">{uploading ? "Завантаження..." : "Додати фото"}</span>
                <input type="file" accept="image/*" multiple hidden disabled={uploading}
                  onChange={(e) => { uploadExitPhotos(e.target.files); e.target.value = ""; }}
                  data-testid="upload-exit-photo-input" />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Можна завантажити необмежену кількість зображень (до 12 МБ кожне).</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold">Роботи</h3>
            <Select value="" onValueChange={addService}>
              <SelectTrigger className="w-56" data-testid="add-service"><SelectValue placeholder="+ Додати послугу" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {fmtCur(s.price, s.price_currency)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <RowTable type="services" />
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold">Запчастини та матеріали</h3>
            <Select value="" onValueChange={addPart}>
              <SelectTrigger className="w-56" data-testid="add-part"><SelectValue placeholder="+ Додати запчастину" /></SelectTrigger>
              <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtCur(p.price, p.price_currency)} ({p.quantity} {p.unit})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <RowTable type="parts" />
        </Card>

        <div className="flex items-center justify-between sticky bottom-0 bg-card border border-border rounded-lg p-4">
          <div className="font-head font-black text-xl">Разом: {fmtTotals(totals)}</div>
          <Button onClick={submit} size="lg" data-testid="save-order-btn">
            <Plus size={18} className="mr-1" /> {editing ? "Зберегти" : "Створити наряд"}
          </Button>
        </div>
      </div>

      <Dialog open={clientDlg} onOpenChange={setClientDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Новий клієнт</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Імʼя *</Label><Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="mt-1.5" data-testid="new-client-name" /></div>
            <div><Label>Телефон</Label><Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="mt-1.5" data-testid="new-client-phone" /></div>
            <div><Label>Email</Label><Input value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="mt-1.5" data-testid="new-client-email" /></div>
          </div>
          <DialogFooter><Button onClick={createClient} data-testid="save-new-client-btn">Створити</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={vehicleDlg} onOpenChange={setVehicleDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Нове авто</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Марка</Label><Input value={newVehicle.make} onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })} className="mt-1.5" data-testid="new-vehicle-make" /></div>
              <div><Label>Модель</Label><Input value={newVehicle.model} onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })} className="mt-1.5" data-testid="new-vehicle-model" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Рік</Label><Input type="number" value={newVehicle.year} onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })} className="mt-1.5" data-testid="new-vehicle-year" /></div>
              <div><Label>Держномер</Label><Input value={newVehicle.plate} onChange={(e) => setNewVehicle({ ...newVehicle, plate: e.target.value })} className="mt-1.5" data-testid="new-vehicle-plate" /></div>
            </div>
            <div><Label>VIN</Label><Input value={newVehicle.vin} onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })} className="mt-1.5" data-testid="new-vehicle-vin" /></div>
            <div><Label>Пробіг, км</Label><Input type="number" value={newVehicle.mileage} onChange={(e) => setNewVehicle({ ...newVehicle, mileage: e.target.value })} className="mt-1.5" data-testid="new-vehicle-mileage" /></div>
            <div>
              <Label>Фотографія авто</Label>
              <div className="mt-1.5">
                {newVehicle.photo ? (
                  <div className="relative w-full h-36 rounded-md overflow-hidden border border-border bg-secondary" data-testid="new-vehicle-photo-preview">
                    <img src={photoUrl(newVehicle.photo, 800)} alt="Авто" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setNewVehicle({ ...newVehicle, photo: null })} data-testid="new-vehicle-remove-photo"
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center">
                      <X size={16} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <label data-testid="new-vehicle-photo-label"
                    className="flex flex-col items-center justify-center gap-1 h-36 rounded-md border-2 border-dashed border-border cursor-pointer text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    <Camera size={26} />
                    <span className="text-xs">{uploading ? "Завантаження..." : "Завантажити фото (одне)"}</span>
                    <input type="file" accept="image/*" hidden disabled={uploading}
                      onChange={async (e) => {
                        const file = e.target.files[0]; e.target.value = "";
                        if (!file) return;
                        setUploading(true);
                        try {
                          const fd = new FormData(); fd.append("file", file);
                          const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setNewVehicle((nv) => ({ ...nv, photo: r.data }));
                          toast.success("Фото завантажено");
                        } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
                        finally { setUploading(false); }
                      }}
                      data-testid="new-vehicle-photo-input" />
                  </label>
                )}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={createVehicle} data-testid="save-new-vehicle-btn">Додати</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batteryDlg} onOpenChange={setBatteryDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Сегменти батареї — {parseInt(form.battery_segments, 10) || 0} S</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Натисніть на сегмент, щоб позначити його як несправний. Обрано: {form.battery_bad_segments.length}
          </p>
          <div className="max-h-[55vh] overflow-y-auto py-2">
            <div className="grid grid-cols-8 sm:grid-cols-10 gap-2" data-testid="battery-grid">
              {Array.from({ length: parseInt(form.battery_segments, 10) || 0 }, (_, i) => i + 1).map((n) => {
                const bad = form.battery_bad_segments.includes(n);
                return (
                  <button key={n} type="button" onClick={() => toggleSegment(n)} data-testid={`segment-${n}`}
                    className={`aspect-square rounded-md border text-sm font-semibold flex items-center justify-center transition-colors ${bad ? "bg-destructive text-white border-destructive" : "bg-secondary border-border hover:border-primary"}`}>
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <Button type="button" variant="ghost" onClick={() => set("battery_bad_segments", [])} data-testid="clear-segments">
              Очистити
            </Button>
            <Button type="button" onClick={() => setBatteryDlg(false)} data-testid="save-segments">Готово</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
